import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { ApiError, api } from "@/api/client";
import type { CompanyContextData } from "@/api/types";

const KEY = ["company-context"] as const;
const KEY_HISTORY = ["company-context-history"] as const;

export interface PromptVersion {
  version: number | null;
  content: string;
  labels: string[];
  commit_message: string | null;
  created_at: string | null;
}

export function useCompanyContext() {
  return useQuery({
    queryKey: KEY,
    queryFn: async () => {
      try {
        return await api.GET<CompanyContextData>("/company-context");
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) return null;
        throw err;
      }
    },
  });
}

export function useUpdateCompanyContext() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (content: string) =>
      api.PUT<CompanyContextData>("/company-context", { content }),
    onSuccess: (data) => {
      qc.setQueryData(KEY, data);
      void qc.invalidateQueries({ queryKey: KEY_HISTORY });
    },
  });
}

export function useCompanyContextHistory(enabled: boolean) {
  return useQuery({
    queryKey: KEY_HISTORY,
    queryFn: () =>
      api.GET<PromptVersion[]>("/company-context/prompt-history"),
    enabled,
  });
}

export function useRollbackCompanyContext() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: { version: number; commit_message?: string }) =>
      api.POST<CompanyContextData>(
        "/company-context/prompt/rollback",
        payload,
      ),
    onSuccess: (data) => {
      qc.setQueryData(KEY, data);
      void qc.invalidateQueries({ queryKey: KEY_HISTORY });
    },
  });
}
